import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, X, Image, Plus, Pencil, Trash2, GripVertical, Check, Mail, Calendar, Clock, Users, Globe, Link, Copy, ExternalLink } from "lucide-react";
import AvailabilityEditor from "@/components/AvailabilityEditor";
import LetterSettings from "@/components/LetterSettings";
import ContextualHelp from "@/components/ContextualHelp";
import { EmailDeliveryCard } from "@/components/EmailDeliveryCard";
import { BillingPlanPanel } from "@/components/BillingPlanPanel";
import { PractitionerRowMenu } from "@/components/PractitionerRowMenu";
import { PractitionerProfileDrawer } from "@/components/PractitionerProfileDrawer";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

const COLOR_PALETTE = ["#1A7A78","#3B6FA0","#B85C32","#7B52A8","#3D8C50","#C48B3F","#B84A5C","#2A8E8A","#8C4DB8","#4A5CB8"];

function ApptTypeRow({ type, index, formTemplates, onUpdate }) {
  const [showPicker, setShowPicker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: type.name, duration_minutes: type.duration_minutes, price: type.price || 0 });
  const currentColor = type.color && !["#205B5A", "#496275", ""].includes(type.color) ? type.color : COLOR_PALETTE[index % COLOR_PALETTE.length];

  const setColor = async (color) => {
    await api.put(`/appointment-types/${type.id}`, { color });
    onUpdate();
    setShowPicker(false);
  };

  const saveEdit = async () => {
    await api.put(`/appointment-types/${type.id}`, editForm);
    setEditing(false);
    onUpdate();
  };

  const archiveType = async () => {
    if (!window.confirm(`Archive "${type.name}"? It will no longer appear in booking options.`)) return;
    await api.delete(`/appointment-types/${type.id}`);
    onUpdate();
  };

  const toggleForm = async (formId) => {
    const current = type.required_form_ids || [];
    const next = current.includes(formId) ? current.filter(id => id !== formId) : [...current, formId];
    await api.put(`/appointment-types/${type.id}`, { required_form_ids: next });
    onUpdate();
  };

  return (
    <div className="px-5 py-4 border-b border-[#1A1B1A]/[0.03] last:border-0">
      <div className="flex items-center gap-4">
        {/* Colour swatch */}
        <div className="relative">
          <button onClick={() => setShowPicker(!showPicker)} data-testid={`color-btn-${type.id}`}
            className="w-8 h-8 rounded-xl border-2 border-white shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            style={{ backgroundColor: currentColor }} />
          {showPicker && (
            <div className="absolute top-10 left-0 z-30 bg-white rounded-2xl shadow-2xl border border-[#1A1B1A]/[0.08] p-4 w-56" data-testid={`color-picker-${type.id}`}>
              <p className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider mb-2.5">Choose colour</p>
              <div className="grid grid-cols-5 gap-2 mb-3">
                {COLOR_PALETTE.map(c => (
                  <button key={c} onClick={() => setColor(c)}
                    className={`w-8 h-8 rounded-xl transition-all ${currentColor === c ? "ring-2 ring-[#1A1B1A] ring-offset-2 scale-105" : "hover:ring-1 hover:ring-[#1A1B1A]/20 hover:ring-offset-1"}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
              <button onClick={() => setShowPicker(false)} className="text-[10px] text-[#757675] hover:text-[#1A1B1A] w-full text-center py-1">Done</button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="flex-1 space-y-2">
            <input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-type-name"
              className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-sm font-medium focus:outline-none focus:border-[#205B5A]" />
            <div className="flex gap-3">
              <div className="flex-1"><label className="text-[9px] text-[#757675]">Duration (min)</label>
                <input type="number" value={editForm.duration_minutes} onChange={e => setEditForm({...editForm, duration_minutes: Number(e.target.value)})}
                  className="w-full px-3 py-1.5 rounded-lg border border-[#1A1B1A]/10 text-xs" /></div>
              <div className="flex-1"><label className="text-[9px] text-[#757675]">Price (£)</label>
                <input type="number" step="0.01" value={editForm.price} onChange={e => setEditForm({...editForm, price: Number(e.target.value)})}
                  className="w-full px-3 py-1.5 rounded-lg border border-[#1A1B1A]/10 text-xs" /></div>
            </div>
            <div className="flex gap-2">
              <button onClick={saveEdit} data-testid="save-type-edit-btn" className="text-[10px] font-medium text-white bg-[#205B5A] px-3 py-1 rounded-full hover:bg-[#174544]">Save</button>
              <button onClick={() => setEditing(false)} className="text-[10px] text-[#757675]">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1">
              <p className="text-sm font-medium text-[#1A1B1A]">{type.name}</p>
              <p className="text-xs text-[#757675]">{type.duration_minutes} min · £{type.price?.toFixed(2)}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => { setEditForm({ name: type.name, duration_minutes: type.duration_minutes, price: type.price || 0 }); setEditing(true); }}
                data-testid={`edit-type-${type.id}`} className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544] px-2 py-1 rounded-lg hover:bg-[#205B5A]/5">Edit</button>
              <button onClick={archiveType} className="text-[10px] text-[#757675] hover:text-red-500 px-2 py-1 rounded-lg">Archive</button>
            </div>
          </>
        )}
      </div>
      {!editing && formTemplates.length > 0 && (
        <div className="mt-3 ml-11">
          <p className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider mb-1.5">Required forms</p>
          <div className="space-y-1">{formTemplates.map(f => (
            <label key={f.id} className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={type.required_form_ids?.includes(f.id) || false} onChange={() => toggleForm(f.id)} className="rounded border-[#1A1B1A]/20 text-[#205B5A] w-3.5 h-3.5" /><span className="text-xs text-[#4A4B4A]">{f.name}</span></label>
          ))}</div>
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { user, clinic, loadUser } = useAuth();
  const [clinicForm, setClinicForm] = useState({ name: "", phone: "", email: "", reply_to_email: "", address: "", website: "", description: "" });
  const [logoUrl, setLogoUrl] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [prefs, setPrefs] = useState({ invoice_prefix: "", currency: "GBP", tax_rate: 0, working_hours_start: "09:00", working_hours_end: "18:00", form_renewal_months: 0, booking_interval: 15, min_booking_notice_hours: 0, max_advance_booking_days: 90, allow_same_day_booking: true, allow_online_booking: false, allow_overlap: false, default_calendar_view: "3-day", show_closed_hours: true });
  const [apptTypes, setApptTypes] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [consentTypes, setConsentTypes] = useState([]);
  const [editingNoteTemplate, setEditingNoteTemplate] = useState(null);
  const [expandedHints, setExpandedHints] = useState({});
  const [editingConsentType, setEditingConsentType] = useState(null);
  const [showCreateType, setShowCreateType] = useState(false);
  const [showCreateConsentType, setShowCreateConsentType] = useState(false);
  const [newTypeForm, setNewTypeForm] = useState({ name: "", duration_minutes: 30, price: 0 });
  const [newConsentForm, setNewConsentForm] = useState({ name: "", description: "", is_marketing_consent: false, is_required: true });
  const [saved, setSaved] = useState("");
  const [tab, setTab] = useState("clinic");
  
  // Team & Availability
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [editingPractitioner, setEditingPractitioner] = useState(null);
  const [teamNotice, setTeamNotice] = useState("");
  
  // Online Booking
  const [bookingSettings, setBookingSettings] = useState(null);
  const [bookingPractitioners, setBookingPractitioners] = useState([]);
  const [bookingSlug, setBookingSlug] = useState('');
  const [slugError, setSlugError] = useState('');
  const [bookingSaved, setBookingSaved] = useState(false);

  useEffect(() => {
    api.get("/settings/clinic").then(r => { const c = r.data; setClinicForm({ name: c.name || "", phone: c.phone || "", email: c.email || "", reply_to_email: c.reply_to_email || "", address: c.address || "", website: c.website || "", description: c.description || "" }); setLogoUrl(c.logo_url || ""); });
    api.get("/settings/preferences").then(r => { const s = r.data; setPrefs(p => ({ ...p, ...s })); });
    api.get("/appointment-types").then(r => setApptTypes(r.data.appointment_types));
    api.get("/forms/templates").then(r => setFormTemplates(r.data.templates)).catch(() => {});
    api.get("/note-templates").then(r => setNoteTemplates(r.data.templates)).catch(() => {});
    api.get("/consent-types").then(r => setConsentTypes(r.data.consent_types)).catch(() => {});
    api.get("/team").then(r => setTeamMembers(r.data.members || [])).catch(() => {});
    api.get("/settings/online-booking").then(r => { 
      setBookingSettings(r.data); 
      setBookingSlug(r.data.clinic_slug || '');
    }).catch(() => {});
    api.get("/settings/online-booking/practitioners").then(r => setBookingPractitioners(r.data.practitioners || [])).catch(() => {});
  }, []);

  const saveClinic = async () => {
    await api.put("/settings/clinic", clinicForm);
    setSaved("clinic"); setTimeout(() => setSaved(""), 2000);
  };

  const savePrefs = async () => {
    await api.put("/settings/preferences", prefs);
    setSaved("prefs"); setTimeout(() => setSaved(""), 2000);
  };

  const isOwner = user?.role === "owner";
  const reloadTeam = (message) => {
    if (typeof message === "string") setTeamNotice(message);
    api.get("/team").then(r => setTeamMembers(r.data.members || [])).catch(() => {});
    loadUser();
  };
  const tabs = [
    { key: "clinic", label: "Clinic" }, 
    { key: "team", label: "Team" },
    { key: "online-booking", label: "Online Booking" },
    { key: "preferences", label: "Preferences" }, 
    { key: "appointment-types", label: "Appointment types" }, 
    { key: "consent", label: "Consent" }, 
    { key: "note-templates", label: "Note templates" },
    ...(["owner", "admin"].includes(user?.role) ? [{ key: "letters", label: "Letters & emails" }] : []),
    ...(isOwner ? [{ key: "billing", label: "Billing & Plan" }] : [])
  ];
  
  const saveBookingSettings = async () => {
    try {
      await api.put("/settings/online-booking", bookingSettings);
      setBookingSaved(true);
      setTimeout(() => setBookingSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save booking settings', err);
    }
  };
  
  const updateBookingSlug = async () => {
    try {
      setSlugError('');
      const { data } = await api.put(`/settings/clinic-slug?slug=${encodeURIComponent(bookingSlug)}`);
      setBookingSettings(prev => ({ ...prev, clinic_slug: data.slug, booking_url: data.booking_url }));
      setBookingSaved(true);
      setTimeout(() => setBookingSaved(false), 2000);
    } catch (err) {
      setSlugError(err.response?.data?.detail || 'Failed to update URL');
    }
  };
  
  const copyBookingUrl = () => {
    navigator.clipboard.writeText(getFullBookingUrl());
  };
  
  // Embed code state
  const [embedCopied, setEmbedCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Get the public app URL from environment or fallback to current origin
  const publicAppUrl = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
  
  const getFullBookingUrl = () => {
    return `${publicAppUrl}${bookingSettings?.booking_url}`;
  };
  
  const getEmbedCode = () => {
    const embedUrl = `${publicAppUrl}${bookingSettings?.booking_url}?embed=true`;
    return `<iframe src="${embedUrl}" width="100%" height="750" style="border:0; border-radius:16px;" loading="lazy" title="Book an appointment"></iframe>`;
  };
  
  const copyEmbedCode = () => {
    navigator.clipboard.writeText(getEmbedCode());
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  };
  
  const copyBookingLink = () => {
    navigator.clipboard.writeText(getFullBookingUrl());
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="settings-page">
      <h1 className="text-2xl font-medium text-[#1A1B1A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Settings</h1>

      <div className="flex gap-1 mb-6 border-b border-[#1A1B1A]/[0.06]">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`settings-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "text-[#205B5A] border-[#205B5A]" : "text-[#757675] border-transparent hover:text-[#1A1B1A]"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "clinic" && (
        <div className="space-y-5">
          {/* Logo upload */}
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6">
            <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-1">Clinic logo</h3>
            <p className="text-[10px] text-[#757675] mb-4">Your logo appears in the app header, invoices, printed documents, and emails.</p>
            {logoUrl ? (
              <div className="flex items-start gap-5">
                <div className="w-48 h-24 bg-[#F8FAF9] rounded-xl border border-[#1A1B1A]/[0.06] flex items-center justify-center p-3">
                  <img src={logoUrl} alt="Clinic logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
                </div>
                <div className="space-y-2 pt-1">
                  <label className="text-[11px] font-medium text-[#205B5A] hover:text-[#174544] cursor-pointer flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#205B5A]/20 hover:bg-[#205B5A]/5 transition-colors">
                    <Upload className="w-3.5 h-3.5" /> Replace logo
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      setLogoUploading(true);
                      const fd = new FormData(); fd.append("file", file);
                      try { const { data } = await api.post("/settings/logo", fd); setLogoUrl(data.logo_url); } catch {}
                      setLogoUploading(false);
                    }} />
                  </label>
                  <button onClick={async () => { await api.delete("/settings/logo"); setLogoUrl(""); }} data-testid="remove-logo-btn"
                    className="text-[11px] text-[#757675] hover:text-red-500 flex items-center gap-1.5 px-3 py-1.5 transition-colors"><X className="w-3.5 h-3.5" /> Remove logo</button>
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-2 w-48 h-24 border-2 border-dashed border-[#1A1B1A]/10 rounded-xl cursor-pointer hover:border-[#205B5A]/30 hover:bg-[#205B5A]/[0.02] transition-colors" data-testid="upload-logo-btn">
                {logoUploading ? <span className="text-xs text-[#757675]">Uploading...</span> : (
                  <><Image className="w-6 h-6 text-[#757675]/50" /><span className="text-[11px] text-[#757675]">Upload clinic logo</span><p className="text-[9px] text-[#B0BEC5]">PNG, JPG, or SVG</p></>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  setLogoUploading(true);
                  const fd = new FormData(); fd.append("file", file);
                  try { const { data } = await api.post("/settings/logo", fd); setLogoUrl(data.logo_url); } catch {}
                  setLogoUploading(false);
                }} />
              </label>
            )}
          </div>

          {/* Clinic details */}
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6 space-y-4">
            {["name", "email", "reply_to_email", "phone", "address", "website", "description"].map(field => (
            <div key={field}>
              <label className="block text-xs font-medium text-[#757675] mb-1 capitalize">
                {field === "reply_to_email" ? "Reply-to email" : field.replace(/_/g, " ")}
              </label>
              {field === "description" ? (
                <textarea value={clinicForm[field]} onChange={e => setClinicForm({...clinicForm, [field]: e.target.value})} rows={3} disabled={!isOwner}
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none disabled:opacity-50" />
              ) : (
                <input value={clinicForm[field]} onChange={e => setClinicForm({...clinicForm, [field]: e.target.value})} disabled={!isOwner}
                  data-testid={`clinic-${field.replace(/_/g, "-")}-input`}
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] disabled:opacity-50" />
              )}
              {field === "reply_to_email" && (
                <p className="text-[10px] text-[#757675] mt-1">
                  Patients see <strong>{clinicForm.name || "your clinic"} via LumeroFlow</strong> as the sender and their
                  replies come here. Emails are sent from notifications@lumeroflow.co.uk so they pass spam checks -
                  leave this blank and replies go to your clinic email instead.
                </p>
              )}
            </div>
          ))}
          {isOwner && (
            <button onClick={saveClinic} data-testid="save-clinic-btn" className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544]">
              {saved === "clinic" ? "Saved!" : "Save changes"}
            </button>
          )}
          </div>
          {isOwner && <EmailDeliveryCard />}
        </div>
      )}

      {/* Team & Availability Tab */}
      {tab === "team" && (
        <div className="space-y-5">
          {teamNotice && (
            <div className="rounded-xl bg-[#205B5A]/[0.06] border border-[#205B5A]/10 px-4 py-3" data-testid="settings-team-notice">
              <p className="text-xs text-[#205B5A]">{teamNotice}</p>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#1A1B1A]/[0.06]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-[#1A1B1A] flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#205B5A]" />
                    Team members & Availability
                  </h3>
                  <p className="text-xs text-[#757675] mt-1">Configure each practitioner's working hours, breaks, and appointment types.</p>
                </div>
                <ContextualHelp helpKey="availability" />
              </div>
            </div>
            
            {teamMembers.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-[#757675]">No team members found.</p>
              </div>
            ) : (
              <div className="divide-y divide-[#1A1B1A]/[0.03]">
                {teamMembers.filter(m => m.role === "owner" || m.role === "practitioner").map(member => (
                  <div key={member.id} className="px-5 py-4 flex items-center justify-between hover:bg-[#FAF9F6] transition-colors"
                    data-testid={`settings-team-row-${member.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#205B5A]/10 flex items-center justify-center">
                        <span className="text-sm font-semibold text-[#205B5A]">
                          {member.full_name?.split(" ").map(n => n[0]).join("").substring(0, 2) || "?"}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#1A1B1A]">{member.full_name}</p>
                        <p className="text-xs text-[#757675]">
                          {member.email} · <span className="capitalize">{member.role}</span>
                          {member.treats_patients && (
                            <> · {member.is_active_practitioner ? "Active practitioner" : "Former practitioner"}</>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedPractitioner({ id: member.id, name: member.full_name })}
                        className="text-xs font-medium text-[#205B5A] hover:text-[#174544] bg-[#205B5A]/5 hover:bg-[#205B5A]/10 px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
                        data-testid={`edit-availability-${member.id}`}
                      >
                        <Calendar className="w-3.5 h-3.5" />
                        Edit availability
                      </button>
                      <PractitionerRowMenu
                        member={member}
                        canManage={isOwner || user?.role === "admin"}
                        isSelf={member.id === user?.id}
                        onEditProfile={() => setEditingPractitioner(member)}
                        onEditTypes={() => setEditingPractitioner(member)}
                        onStatusChanged={reloadTeam}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="bg-[#FAF9F6] rounded-xl p-4">
            <p className="text-xs text-[#757675]">
              <strong>Tip:</strong> Configure each practitioner's availability to enable accurate booking and future online patient booking.
            </p>
          </div>
        </div>
      )}

      {/* Online Booking Tab */}
      {tab === "online-booking" && bookingSettings && (
        <div className="space-y-5">
          {/* Enable/Disable */}
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[#1A1B1A] flex items-center gap-2">
                  <Globe className="w-4 h-4 text-[#205B5A]" />
                  Online Booking
                </h3>
                <p className="text-xs text-[#757675] mt-1">Allow patients to book appointments directly from your public booking page.</p>
              </div>
              <ContextualHelp helpKey="online-booking" />
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={bookingSettings.enabled}
                  onChange={(e) => setBookingSettings({ ...bookingSettings, enabled: e.target.checked })}
                  className="sr-only peer"
                  data-testid="online-booking-toggle"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#205B5A]"></div>
              </label>
            </div>
            
            {bookingSettings.enabled && (
              <div className="mt-4 pt-4 border-t border-[#1A1B1A]/[0.06]">
                <label className="block text-xs font-medium text-[#757675] mb-1.5">Booking URL</label>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center bg-[#FAF9F6] rounded-lg px-3 py-2 border border-[#1A1B1A]/10">
                    <span className="text-xs text-[#757675] mr-1">{publicAppUrl}/book/</span>
                    <input
                      type="text"
                      value={bookingSlug}
                      onChange={(e) => setBookingSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      className="flex-1 bg-transparent text-sm font-medium text-[#1A1B1A] focus:outline-none"
                      placeholder="your-clinic"
                      data-testid="booking-slug-input"
                    />
                  </div>
                  <button
                    onClick={updateBookingSlug}
                    disabled={bookingSlug === bookingSettings.clinic_slug}
                    className="px-4 py-2 text-xs font-medium bg-[#205B5A] text-white rounded-lg hover:bg-[#174544] disabled:bg-[#205B5A]/50 disabled:cursor-not-allowed transition-colors"
                    data-testid="update-slug-btn"
                  >
                    Update
                  </button>
                  <button
                    onClick={copyBookingUrl}
                    className="px-3 py-2 text-xs font-medium text-[#205B5A] bg-[#205B5A]/5 rounded-lg hover:bg-[#205B5A]/10 transition-colors flex items-center gap-1"
                    title="Copy booking URL"
                    data-testid="copy-booking-url-btn"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <a
                    href={getFullBookingUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 text-xs font-medium text-[#205B5A] bg-[#205B5A]/5 rounded-lg hover:bg-[#205B5A]/10 transition-colors flex items-center gap-1"
                    title="Preview booking page"
                    data-testid="preview-booking-btn"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
                {slugError && <p className="text-xs text-red-500 mt-1">{slugError}</p>}
              </div>
            )}
          </div>
          
          {bookingSettings.enabled && (
            <>
              {/* Page Settings */}
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6 space-y-4">
                <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider">Page Settings</h3>
                
                <div>
                  <label className="block text-xs font-medium text-[#757675] mb-1">Page Title</label>
                  <input
                    type="text"
                    value={bookingSettings.page_title || ''}
                    onChange={(e) => setBookingSettings({ ...bookingSettings, page_title: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]"
                    placeholder="Book an Appointment"
                    data-testid="booking-page-title"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-[#757675] mb-1">Confirmation Message</label>
                  <textarea
                    value={bookingSettings.confirmation_message || ''}
                    onChange={(e) => setBookingSettings({ ...bookingSettings, confirmation_message: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]"
                    placeholder="Thank you for booking. We look forward to seeing you!"
                    data-testid="booking-confirmation-message"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-[#757675] mb-1">Cancellation Policy</label>
                  <textarea
                    value={bookingSettings.cancellation_policy || ''}
                    onChange={(e) => setBookingSettings({ ...bookingSettings, cancellation_policy: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]"
                    placeholder="Please give at least 24 hours notice for cancellations."
                    data-testid="booking-cancellation-policy"
                  />
                </div>
              </div>
              
              {/* Booking Rules */}
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6 space-y-4">
                <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider">Booking Rules</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#757675] mb-1">Minimum Notice (hours)</label>
                    <input
                      type="number"
                      min="0"
                      value={bookingSettings.min_notice_hours || 2}
                      onChange={(e) => setBookingSettings({ ...bookingSettings, min_notice_hours: Number(e.target.value) })}
                      className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]"
                      data-testid="booking-min-notice"
                    />
                    <p className="text-[10px] text-[#757675] mt-1">How far in advance patients must book</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#757675] mb-1">Max Advance Days</label>
                    <input
                      type="number"
                      min="1"
                      value={bookingSettings.max_advance_days || 60}
                      onChange={(e) => setBookingSettings({ ...bookingSettings, max_advance_days: Number(e.target.value) })}
                      className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]"
                      data-testid="booking-max-advance"
                    />
                    <p className="text-[10px] text-[#757675] mt-1">How far ahead patients can book</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bookingSettings.allow_any_practitioner}
                      onChange={(e) => setBookingSettings({ ...bookingSettings, allow_any_practitioner: e.target.checked })}
                      className="rounded border-[#1A1B1A]/20 text-[#205B5A] w-4 h-4"
                      data-testid="booking-allow-any"
                    />
                    <span className="text-sm text-[#4A4B4A]">Allow "Any Available Practitioner" option</span>
                  </label>
                </div>
                
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bookingSettings.require_dob}
                      onChange={(e) => setBookingSettings({ ...bookingSettings, require_dob: e.target.checked })}
                      className="rounded border-[#1A1B1A]/20 text-[#205B5A] w-4 h-4"
                      data-testid="booking-require-dob"
                    />
                    <span className="text-sm text-[#4A4B4A]">Require date of birth</span>
                  </label>
                </div>
              </div>
              
              {/* Appointment Types */}
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6">
                <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Available Services</h3>
                <p className="text-xs text-[#757675] mb-4">Select which appointment types patients can book online.</p>
                
                <div className="space-y-2">
                  {apptTypes.map(type => (
                    <label key={type.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#FAF9F6] cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={bookingSettings.appointment_type_ids?.includes(type.id) || false}
                        onChange={(e) => {
                          const current = bookingSettings.appointment_type_ids || [];
                          const next = e.target.checked 
                            ? [...current, type.id]
                            : current.filter(id => id !== type.id);
                          setBookingSettings({ ...bookingSettings, appointment_type_ids: next });
                        }}
                        className="rounded border-[#1A1B1A]/20 text-[#205B5A] w-4 h-4"
                        data-testid={`booking-type-${type.id}`}
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-[#1A1B1A]">{type.name}</span>
                        <span className="text-xs text-[#757675] ml-2">{type.duration_minutes} min · £{type.price?.toFixed(2)}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Practitioners */}
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6">
                <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Available Practitioners</h3>
                <p className="text-xs text-[#757675] mb-4">Select which practitioners are available for online booking.</p>
                
                <div className="space-y-2">
                  {bookingPractitioners.map(prac => (
                    <label key={prac.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#FAF9F6] cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={bookingSettings.practitioner_ids?.includes(prac.id) || false}
                        onChange={(e) => {
                          const current = bookingSettings.practitioner_ids || [];
                          const next = e.target.checked 
                            ? [...current, prac.id]
                            : current.filter(id => id !== prac.id);
                          setBookingSettings({ ...bookingSettings, practitioner_ids: next });
                        }}
                        className="rounded border-[#1A1B1A]/20 text-[#205B5A] w-4 h-4"
                        data-testid={`booking-prac-${prac.id}`}
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-[#1A1B1A]">{prac.name}</span>
                        {!prac.has_schedule && (
                          <span className="text-xs text-amber-600 ml-2">(No availability configured)</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Embed & Website Section */}
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6">
                <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-4">Embed & Website</h3>
                <p className="text-xs text-[#757675] mb-5">Add online booking to your website using a link or embed code.</p>
                
                <div className="space-y-6">
                  {/* Booking Link Card */}
                  <div className="bg-[#FAF9F6] rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Link className="w-4 h-4 text-[#205B5A]" />
                      <h4 className="text-sm font-medium text-[#1A1B1A]">Booking Link</h4>
                    </div>
                    <p className="text-xs text-[#757675] mb-3">Share this link directly or add a "Book Appointment" button to your website.</p>
                    
                    <div className="bg-white rounded-lg border border-[#1A1B1A]/10 px-3 py-2.5 mb-3">
                      <code className="text-xs text-[#4A4B4A] break-all" data-testid="booking-link-display">
                        {getFullBookingUrl()}
                      </code>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={copyBookingLink}
                        className="flex-1 px-4 py-2 text-xs font-medium bg-[#205B5A] text-white rounded-lg hover:bg-[#174544] transition-colors flex items-center justify-center gap-2"
                        data-testid="copy-booking-link-btn"
                      >
                        {linkCopied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Link</>}
                      </button>
                      <a
                        href={getFullBookingUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 text-xs font-medium text-[#205B5A] bg-white border border-[#205B5A]/20 rounded-lg hover:bg-[#205B5A]/5 transition-colors flex items-center gap-2"
                        data-testid="preview-link-btn"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Preview
                      </a>
                    </div>
                  </div>
                  
                  {/* Embed Code Card */}
                  <div className="bg-[#FAF9F6] rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Globe className="w-4 h-4 text-[#205B5A]" />
                      <h4 className="text-sm font-medium text-[#1A1B1A]">Embed on Website</h4>
                    </div>
                    <p className="text-xs text-[#757675] mb-3">Copy this code and paste it into your website where you want the booking form to appear.</p>
                    
                    <div className="bg-[#1A1B1A] rounded-lg p-3 mb-3 overflow-x-auto">
                      <code className="text-xs text-green-400 whitespace-pre-wrap break-all" data-testid="embed-code-display">
                        {getEmbedCode()}
                      </code>
                    </div>
                    
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={copyEmbedCode}
                        className="flex-1 px-4 py-2 text-xs font-medium bg-[#205B5A] text-white rounded-lg hover:bg-[#174544] transition-colors flex items-center justify-center gap-2"
                        data-testid="copy-embed-code-btn"
                      >
                        {embedCopied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Embed Code</>}
                      </button>
                      <a
                        href={`${publicAppUrl}${bookingSettings?.booking_url}?embed=true`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 text-xs font-medium text-[#205B5A] bg-white border border-[#205B5A]/20 rounded-lg hover:bg-[#205B5A]/5 transition-colors flex items-center gap-2"
                        data-testid="preview-embed-btn"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Preview Embed
                      </a>
                    </div>
                    
                    {/* Website Builder Instructions */}
                    <div className="border-t border-[#1A1B1A]/10 pt-4">
                      <h5 className="text-xs font-medium text-[#4A4B4A] mb-2">How to add to your website:</h5>
                      <div className="space-y-2 text-xs text-[#757675]">
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-[#4A4B4A] w-20 flex-shrink-0">WordPress:</span>
                          <span>Add a Custom HTML block and paste the embed code.</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-[#4A4B4A] w-20 flex-shrink-0">Wix:</span>
                          <span>Add an Embed element → Custom Embeds → Embed a Site, then paste the code.</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-[#4A4B4A] w-20 flex-shrink-0">Squarespace:</span>
                          <span>Add a Code Block and paste the embed code.</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-[#4A4B4A] w-20 flex-shrink-0">Webflow:</span>
                          <span>Add an Embed element and paste the code.</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-[#4A4B4A] w-20 flex-shrink-0">Custom:</span>
                          <span>Paste the iframe code into your HTML where you want the booking form.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={saveBookingSettings}
                  className="px-6 py-2.5 text-sm font-medium bg-[#205B5A] text-white rounded-xl hover:bg-[#174544] transition-colors flex items-center gap-2"
                  data-testid="save-booking-settings-btn"
                >
                  {bookingSaved ? <><Check className="w-4 h-4" /> Saved!</> : 'Save Settings'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {editingPractitioner && (
        <PractitionerProfileDrawer
          practitionerId={editingPractitioner.id}
          canManage={isOwner || user?.role === "admin"}
          onClose={() => setEditingPractitioner(null)}
          onSaved={() => { setTeamNotice("Practitioner profile updated."); reloadTeam(); }}
        />
      )}

      {/* Availability Editor Modal */}
      {selectedPractitioner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <AvailabilityEditor
              practitionerId={selectedPractitioner.id}
              practitionerName={selectedPractitioner.name}
              onClose={() => setSelectedPractitioner(null)}
            />
          </div>
        </div>
      )}

      {tab === "preferences" && (
        <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-[#757675] mb-1">Invoice prefix</label>
              <input value={prefs.invoice_prefix} onChange={e => setPrefs({...prefs, invoice_prefix: e.target.value})} disabled={!isOwner}
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] disabled:opacity-50" /></div>
            <div><label className="block text-xs font-medium text-[#757675] mb-1">Currency</label>
              <select value={prefs.currency} onChange={e => setPrefs({...prefs, currency: e.target.value})} disabled={!isOwner}
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] disabled:opacity-50">
                <option value="GBP">GBP (£)</option><option value="USD">USD ($)</option><option value="EUR">EUR (€)</option>
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-[#757675] mb-1">Working hours start</label>
              <input type="time" value={prefs.working_hours_start} onChange={e => setPrefs({...prefs, working_hours_start: e.target.value})} disabled={!isOwner}
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] disabled:opacity-50" /></div>
            <div><label className="block text-xs font-medium text-[#757675] mb-1">Working hours end</label>
              <input type="time" value={prefs.working_hours_end} onChange={e => setPrefs({...prefs, working_hours_end: e.target.value})} disabled={!isOwner}
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] disabled:opacity-50" /></div>
          </div>
          {/* Form renewal setting */}
          <div className="mt-4 pt-4 border-t border-[#1A1B1A]/[0.06]">
            <h4 className="text-xs font-semibold text-[#1A1B1A] mb-2">Form renewal</h4>
            <p className="text-[10px] text-[#757675] mb-2">Require patients to re-complete forms if they haven't visited for longer than this period.</p>
            <select value={prefs.form_renewal_months} onChange={e => setPrefs({...prefs, form_renewal_months: Number(e.target.value)})} disabled={!isOwner}
              className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm disabled:opacity-50">
              <option value={0}>Disabled</option><option value={6}>6 months</option><option value={12}>12 months</option><option value={24}>24 months</option><option value={36}>36 months</option>
            </select>
          </div>

          {/* Booking settings */}
          <div className="mt-4 pt-4 border-t border-[#1A1B1A]/[0.06]">
            <h4 className="text-xs font-semibold text-[#1A1B1A] mb-3">Booking & scheduling</h4>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-xs font-medium text-[#757675] mb-1">Booking interval</label>
                <select value={prefs.booking_interval} onChange={e => setPrefs({...prefs, booking_interval: Number(e.target.value)})} disabled={!isOwner} data-testid="booking-interval-setting"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm disabled:opacity-50">
                  {[5,10,15,20,30,45,60].map(m => <option key={m} value={m}>{m} minutes</option>)}
                </select></div>
              <div><label className="block text-xs font-medium text-[#757675] mb-1">Max advance booking (days)</label>
                <input type="number" value={prefs.max_advance_booking_days || 90} onChange={e => setPrefs({...prefs, max_advance_booking_days: Number(e.target.value)})} disabled={!isOwner}
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm disabled:opacity-50" /></div>
              <div><label className="block text-xs font-medium text-[#757675] mb-1">Min booking notice (hours)</label>
                <input type="number" value={prefs.min_booking_notice_hours || 0} onChange={e => setPrefs({...prefs, min_booking_notice_hours: Number(e.target.value)})} disabled={!isOwner}
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm disabled:opacity-50" /></div>
              <div><label className="block text-xs font-medium text-[#757675] mb-1">Buffer between appts (min)</label>
                <input type="number" value={prefs.booking_buffer_minutes || 0} onChange={e => setPrefs({...prefs, booking_buffer_minutes: Number(e.target.value)})} disabled={!isOwner}
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm disabled:opacity-50" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <label className="flex items-center gap-2"><input type="checkbox" checked={prefs.allow_same_day_booking !== false} onChange={e => setPrefs({...prefs, allow_same_day_booking: e.target.checked})} disabled={!isOwner} className="rounded border-[#1A1B1A]/20 text-[#205B5A]" /><span className="text-xs text-[#4A4B4A]">Same-day booking</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={prefs.allow_online_booking || false} onChange={e => setPrefs({...prefs, allow_online_booking: e.target.checked})} disabled={!isOwner} className="rounded border-[#1A1B1A]/20 text-[#205B5A]" /><span className="text-xs text-[#4A4B4A]">Online booking</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={prefs.allow_overlap || false} onChange={e => setPrefs({...prefs, allow_overlap: e.target.checked})} disabled={!isOwner} className="rounded border-[#1A1B1A]/20 text-[#205B5A]" /><span className="text-xs text-[#4A4B4A]">Allow overlap</span></label>
            </div>
          </div>
          {isOwner && (
            <button onClick={savePrefs} data-testid="save-prefs-btn" className="mt-4 bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544]">
              {saved === "prefs" ? "Saved!" : "Save preferences"}
            </button>
          )}
        </div>
      )}

      {tab === "appointment-types" && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#757675]">Calendar colours are assigned automatically. Click a colour to customise.</p>
            <div className="flex gap-2">
              <button onClick={async () => {
                for (const t of apptTypes) { await api.put(`/appointment-types/${t.id}`, { color: "" }); }
                const { data } = await api.get("/appointment-types"); setApptTypes(data.appointment_types);
              }} className="text-[10px] font-medium text-[#757675] hover:text-[#205B5A]">Reset colours</button>
              <button onClick={() => setShowCreateType(true)} data-testid="create-appt-type-btn"
                className="bg-[#205B5A] text-white px-3.5 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#174544] flex items-center gap-1">+ New type</button>
            </div>
          </div>

          {/* Create new type form */}
          {showCreateType && (
            <div className="bg-[#FAF9F6] rounded-2xl border border-[#1A1B1A]/[0.06] p-5 mb-3">
              <p className="text-xs font-semibold text-[#1A1B1A] mb-3" style={H}>New appointment type</p>
              <div className="space-y-2">
                <input value={newTypeForm.name} onChange={e => setNewTypeForm({...newTypeForm, name: e.target.value})} placeholder="Type name (e.g. Sports Rehab, Hijama, Injection)" data-testid="new-type-name"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[10px] text-[#757675] mb-1 block">Duration (minutes)</label>
                    <select value={newTypeForm.duration_minutes} onChange={e => setNewTypeForm({...newTypeForm, duration_minutes: Number(e.target.value)})}
                      className="w-full px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-sm">
                      {[15,20,30,45,60,75,90,120].map(m => <option key={m} value={m}>{m} min</option>)}
                    </select></div>
                  <div><label className="text-[10px] text-[#757675] mb-1 block">Price (£)</label>
                    <input type="number" step="0.01" value={newTypeForm.price} onChange={e => setNewTypeForm({...newTypeForm, price: Number(e.target.value)})}
                      className="w-full px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-sm" /></div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={async () => {
                    if (!newTypeForm.name.trim()) return;
                    await api.post("/appointment-types", newTypeForm);
                    setShowCreateType(false); setNewTypeForm({ name: "", duration_minutes: 30, price: 0 });
                    const { data } = await api.get("/appointment-types"); setApptTypes(data.appointment_types);
                  }} data-testid="save-new-type-btn" className="text-[11px] font-medium text-white bg-[#205B5A] px-4 py-1.5 rounded-full hover:bg-[#174544]">Create type</button>
                  <button onClick={() => setShowCreateType(false)} className="text-[11px] text-[#757675]">Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
          {apptTypes.map((t, tIdx) => (
            <ApptTypeRow key={t.id} type={t} index={tIdx} formTemplates={formTemplates} onUpdate={async () => { const { data } = await api.get("/appointment-types"); setApptTypes(data.appointment_types); }} />
          ))}
          {apptTypes.length === 0 && <p className="p-8 text-center text-sm text-[#757675]">No appointment types configured</p>}
        </div>
        </div>
      )}

      {/* Consent Types tab */}
      {tab === "consent" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-[#757675]">{consentTypes.length} consent types configured</p>
              <p className="text-[10px] text-[#B0BEC5]">Define the consent requirements for your clinic. Mark one as marketing consent to link with the marketing audience.</p>
            </div>
            <button onClick={() => setShowCreateConsentType(true)} data-testid="create-consent-type-btn"
              className="bg-[#205B5A] text-white px-3.5 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#174544] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> New consent type</button>
          </div>

          {/* Create new consent type form */}
          {showCreateConsentType && (
            <div className="bg-[#FAF9F6] rounded-2xl border border-[#1A1B1A]/[0.06] p-5 mb-4">
              <p className="text-xs font-semibold text-[#1A1B1A] mb-3" style={H}>New consent type</p>
              <div className="space-y-3">
                <input value={newConsentForm.name} onChange={e => setNewConsentForm({...newConsentForm, name: e.target.value})} placeholder="Consent type name (e.g. Photography consent, AI documentation)" data-testid="new-consent-name"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
                <textarea value={newConsentForm.description} onChange={e => setNewConsentForm({...newConsentForm, description: e.target.value})} placeholder="Description (optional)" rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-xs focus:outline-none focus:border-[#205B5A] resize-none" />
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={newConsentForm.is_required} onChange={e => setNewConsentForm({...newConsentForm, is_required: e.target.checked})}
                      className="rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                    <span className="text-xs text-[#4A4B4A]">Required</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={newConsentForm.is_marketing_consent} onChange={e => setNewConsentForm({...newConsentForm, is_marketing_consent: e.target.checked})}
                      className="rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                    <span className="text-xs text-[#4A4B4A] flex items-center gap-1"><Mail className="w-3 h-3" /> Marketing consent</span>
                  </label>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={async () => {
                    if (!newConsentForm.name.trim()) return;
                    await api.post("/consent-types", newConsentForm);
                    setShowCreateConsentType(false); setNewConsentForm({ name: "", description: "", is_marketing_consent: false, is_required: true });
                    const { data } = await api.get("/consent-types"); setConsentTypes(data.consent_types);
                  }} data-testid="save-new-consent-btn" className="text-[11px] font-medium text-white bg-[#205B5A] px-4 py-1.5 rounded-full hover:bg-[#174544]">Create consent type</button>
                  <button onClick={() => setShowCreateConsentType(false)} className="text-[11px] text-[#757675]">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Edit consent type form */}
          {editingConsentType && (
            <div className="bg-[#FAF9F6] rounded-2xl border border-[#1A1B1A]/[0.06] p-5 mb-4">
              <p className="text-xs font-semibold text-[#1A1B1A] mb-3" style={H}>Edit consent type</p>
              <div className="space-y-3">
                <input value={editingConsentType.name} onChange={e => setEditingConsentType({...editingConsentType, name: e.target.value})} data-testid="edit-consent-name"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
                <textarea value={editingConsentType.description || ""} onChange={e => setEditingConsentType({...editingConsentType, description: e.target.value})} placeholder="Description (optional)" rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-xs focus:outline-none focus:border-[#205B5A] resize-none" />
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={editingConsentType.is_required} onChange={e => setEditingConsentType({...editingConsentType, is_required: e.target.checked})}
                      className="rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                    <span className="text-xs text-[#4A4B4A]">Required</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={editingConsentType.is_marketing_consent} onChange={e => setEditingConsentType({...editingConsentType, is_marketing_consent: e.target.checked})}
                      className="rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                    <span className="text-xs text-[#4A4B4A] flex items-center gap-1"><Mail className="w-3 h-3" /> Marketing consent</span>
                  </label>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={async () => {
                    await api.put(`/consent-types/${editingConsentType.id}`, { name: editingConsentType.name, description: editingConsentType.description, is_marketing_consent: editingConsentType.is_marketing_consent, is_required: editingConsentType.is_required });
                    setEditingConsentType(null);
                    const { data } = await api.get("/consent-types"); setConsentTypes(data.consent_types);
                  }} data-testid="save-consent-edit-btn" className="text-[11px] font-medium text-white bg-[#205B5A] px-4 py-1.5 rounded-full hover:bg-[#174544]">Save changes</button>
                  <button onClick={() => setEditingConsentType(null)} className="text-[11px] text-[#757675]">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Consent types list */}
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
            {consentTypes.map((ct, idx) => (
              <div key={ct.id} className="px-5 py-4 border-b border-[#1A1B1A]/[0.03] last:border-0 flex items-center gap-4" data-testid={`consent-type-${ct.id}`}>
                <div className="text-[#B0BEC5] cursor-grab"><GripVertical className="w-4 h-4" /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-[#1A1B1A]">{ct.name}</p>
                    {ct.is_marketing_consent && (
                      <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 flex items-center gap-1"><Mail className="w-2.5 h-2.5" /> Marketing</span>
                    )}
                    {ct.is_required && (
                      <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">Required</span>
                    )}
                  </div>
                  {ct.description && <p className="text-[10px] text-[#757675] mt-0.5">{ct.description}</p>}
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => setEditingConsentType({...ct})} className="text-[10px] font-medium text-[#205B5A] px-2 py-1 rounded-lg hover:bg-[#205B5A]/5 flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                  <button onClick={async () => {
                    if (!window.confirm(`Delete "${ct.name}"? Existing patient consent records will remain.`)) return;
                    await api.delete(`/consent-types/${ct.id}`);
                    const { data } = await api.get("/consent-types"); setConsentTypes(data.consent_types);
                  }} className="text-[10px] text-[#757675] hover:text-red-500 px-2 py-1 rounded-lg flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            ))}
            {consentTypes.length === 0 && <p className="p-8 text-center text-sm text-[#757675]">No consent types configured. Add your clinic's consent requirements.</p>}
          </div>
        </div>
      )}

      {/* Letters & emails tab */}
      {tab === "letters" && <LetterSettings />}
      {tab === "billing" && <BillingPlanPanel />}

      {/* Note Templates tab */}
      {tab === "note-templates" && (
        <div>
          {editingNoteTemplate ? (
            <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6">
              <div className="flex items-center justify-between mb-4">
                <input value={editingNoteTemplate.name} onChange={e => setEditingNoteTemplate({...editingNoteTemplate, name: e.target.value})}
                  className="text-lg font-medium text-[#1A1B1A] bg-transparent border-none outline-none" style={H} data-testid="template-name-input" />
                <div className="flex gap-2">
                  <button onClick={() => { setEditingNoteTemplate(null); setExpandedHints({}); }} className="text-xs text-[#757675]">Cancel</button>
                  <button onClick={async () => {
                    if (editingNoteTemplate.id) { await api.put(`/note-templates/${editingNoteTemplate.id}`, editingNoteTemplate); }
                    else { await api.post("/note-templates", editingNoteTemplate); }
                    setEditingNoteTemplate(null);
                    setExpandedHints({});
                    api.get("/note-templates").then(r => setNoteTemplates(r.data.templates));
                  }} data-testid="save-note-template-btn" className="bg-[#205B5A] text-white px-4 py-1.5 rounded-full text-xs font-medium hover:bg-[#174544]">Save template</button>
                </div>
              </div>
              <textarea value={editingNoteTemplate.description || ""} onChange={e => setEditingNoteTemplate({...editingNoteTemplate, description: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs mb-4 resize-none" rows={2} placeholder="Template description..." />
              <p className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider mb-2">Sections</p>
              <div className="space-y-2 mb-3">
                {(editingNoteTemplate.sections || []).map((section, i) => (
                  <div key={section.id || i} className="bg-[#FAF9F6] rounded-lg p-2.5" data-testid={`section-${i}`}>
                   <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#757675] w-5 text-center">{i + 1}</span>
                    <input value={section.label} onChange={e => { const s = [...editingNoteTemplate.sections]; s[i] = {...s[i], label: e.target.value}; setEditingNoteTemplate({...editingNoteTemplate, sections: s}); }} className="flex-1 px-2 py-1.5 rounded-md border border-[#1A1B1A]/10 text-xs" />
                    <select value={section.semantic_type} onChange={e => { const s = [...editingNoteTemplate.sections]; s[i] = {...s[i], semantic_type: e.target.value}; setEditingNoteTemplate({...editingNoteTemplate, sections: s}); }} className="px-2 py-1.5 rounded-md border border-[#1A1B1A]/10 text-[10px] w-36">
                      {["current_primary_issue","symptom_progression","trauma_history","surgery_history","medical_conditions","medication","systems_review","examination_findings","treatment_given","follow_up_plan","clinician_notes","free_text"].map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                    </select>
                    <div className="flex gap-0.5">
                      <button onClick={() => { if (i > 0) { const s = [...editingNoteTemplate.sections]; [s[i], s[i-1]] = [s[i-1], s[i]]; setEditingNoteTemplate({...editingNoteTemplate, sections: s}); }}} className="text-[#757675] hover:text-[#1A1B1A] text-[10px] px-1">up</button>
                      <button onClick={() => { if (i < editingNoteTemplate.sections.length - 1) { const s = [...editingNoteTemplate.sections]; [s[i], s[i+1]] = [s[i+1], s[i]]; setEditingNoteTemplate({...editingNoteTemplate, sections: s}); }}} className="text-[#757675] hover:text-[#1A1B1A] text-[10px] px-1">dn</button>
                      <button onClick={() => setEditingNoteTemplate({...editingNoteTemplate, sections: editingNoteTemplate.sections.filter((_, j) => j !== i)})} className="text-red-400 hover:text-red-500 text-[10px] px-1">x</button>
                    </div>
                   </div>
                   <button
                     onClick={() => setExpandedHints(h => ({ ...h, [i]: !h[i] }))}
                     data-testid={`ai-hint-toggle-${i}`}
                     className="mt-1.5 ml-7 text-[10px] text-[#757675] hover:text-[#205B5A]"
                   >
                     {expandedHints[i] ? "Hide AI guidance" : (section.ai_hint ? "AI guidance set" : "+ AI field hint (optional)")}
                   </button>
                   {expandedHints[i] && (
                     <textarea
                       value={section.ai_hint || ""}
                       onChange={e => { const sec = [...editingNoteTemplate.sections]; sec[i] = {...sec[i], ai_hint: e.target.value}; setEditingNoteTemplate({...editingNoteTemplate, sections: sec}); }}
                       data-testid={`ai-hint-input-${i}`}
                       rows={2}
                       placeholder="Tell the AI what belongs in this field, e.g. Use this field for respiratory, cardiovascular, neurological, gastrointestinal and genitourinary screening information."
                       className="mt-1 ml-7 w-[calc(100%-1.75rem)] px-2 py-1.5 rounded-md border border-[#1A1B1A]/10 text-[11px] resize-y"
                     />
                   )}
                  </div>
                ))}
              </div>
              <button onClick={() => setEditingNoteTemplate({...editingNoteTemplate, sections: [...(editingNoteTemplate.sections || []), { id: crypto.randomUUID(), label: "", semantic_type: "free_text", field_type: "long_text", ai_hint: "" }]})}
                data-testid="add-section-btn" className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544]">+ Add section</button>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-[#757675]">{noteTemplates.length} templates</p>
                <button onClick={() => setEditingNoteTemplate({ name: "New Template", description: "", sections: [{ id: crypto.randomUUID(), label: "Notes", semantic_type: "free_text", field_type: "long_text" }] })}
                  data-testid="create-note-template-btn" className="bg-[#205B5A] text-white px-3.5 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#174544]">+ New template</button>
              </div>
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
                {noteTemplates.map(t => (
                  <div key={t.id} className="px-5 py-4 border-b border-[#1A1B1A]/[0.03] last:border-0 flex items-center gap-4">
                    <div className="flex-1"><p className="text-sm font-medium text-[#1A1B1A]">{t.name}</p><p className="text-[10px] text-[#757675]">{t.sections?.length || 0} sections{t.description ? ` · ${t.description}` : ""}</p></div>
                    <div className="flex gap-1.5">
                      <button onClick={() => { setEditingNoteTemplate({...t}); setExpandedHints({}); }} data-testid={`edit-template-${t.id}`} className="text-[10px] font-medium text-[#205B5A] px-2 py-1 rounded-lg hover:bg-[#205B5A]/5">Edit</button>
                      <button onClick={async () => { await api.post(`/note-templates/${t.id}/duplicate`); api.get("/note-templates").then(r => setNoteTemplates(r.data.templates)); }} className="text-[10px] text-[#4A4B4A] px-2 py-1 rounded-lg hover:bg-[#FAF9F6]">Duplicate</button>
                      <button onClick={async () => { await api.delete(`/note-templates/${t.id}`); api.get("/note-templates").then(r => setNoteTemplates(r.data.templates)); }} className="text-[10px] text-[#757675] hover:text-red-500 px-2 py-1">Archive</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
